import { useState, useEffect, useCallback } from 'react';
import {
  Monitor, Users, Clock, Eye, TrendingUp, RefreshCw,
  ArrowUpRight,
} from 'lucide-react';
import apiClient from '../lib/api';

interface DAUWAUMAUMetrics {
  dau: number;
  wau: number;
  mau: number;
  dauByRole: Record<string, number>;
}

interface LoginActivitySummary {
  totalLogins: number;
  uniqueUsers: number;
  byRole: Record<string, number>;
}

interface UserLoginDetail {
  userId: string;
  email: string;
  role: string;
  firstName?: string;
  lastName?: string;
  loginCount: number;
  lastLoginAt: string;
}

interface SessionSummary {
  totalSessions: number;
  uniqueUsers: number;
  avgDurationSeconds: number;
  medianDurationSeconds: number;
  avgPageViews: number;
}

interface PageViewSummary {
  path: string;
  views: number;
  uniqueUsers: number;
}

interface LoginTrendEntry {
  date: string;
  logins: number;
  uniqueUsers: number;
}

type DateRange = '7d' | '14d' | '30d' | '90d';

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  supervisor: 'Supervisor',
  distributor: 'Account Manager',
  physician: 'Physician',
  physician_readonly: 'Physician (RO)',
};

const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-800',
  supervisor: 'bg-orange-100 text-orange-800',
  distributor: 'bg-blue-100 text-blue-800',
  physician: 'bg-green-100 text-green-800',
  physician_readonly: 'bg-gray-100 text-gray-800',
};

export default function AdminUserActivity() {
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [loading, setLoading] = useState(true);
  const [dauMetrics, setDauMetrics] = useState<DAUWAUMAUMetrics | null>(null);
  const [loginSummary, setLoginSummary] = useState<LoginActivitySummary | null>(null);
  const [loginDetails, setLoginDetails] = useState<UserLoginDetail[]>([]);
  const [sessionSummary, setSessionSummary] = useState<SessionSummary | null>(null);
  const [topPages, setTopPages] = useState<PageViewSummary[]>([]);
  const [loginTrend, setLoginTrend] = useState<LoginTrendEntry[]>([]);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<'loginCount' | 'lastLoginAt'>('loginCount');

  const getDateParams = useCallback(() => {
    const days = parseInt(dateRange);
    const endDate = new Date().toISOString();
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    return { startDate, endDate };
  }, [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { startDate, endDate } = getDateParams();
    const params = `startDate=${startDate}&endDate=${endDate}`;

    try {
      const [dauRes, loginRes, detailRes, sessionRes, pagesRes, trendRes] = await Promise.all([
        apiClient.get<any>('/activity/analytics/dau-wau-mau'),
        apiClient.get<any>(`/activity/analytics/logins?${params}`),
        apiClient.get<any>(`/activity/analytics/login-details?${params}&sortBy=${sortBy}${roleFilter ? `&role=${roleFilter}` : ''}&limit=50`),
        apiClient.get<any>(`/activity/analytics/sessions?${params}`),
        apiClient.get<any>(`/activity/analytics/top-pages?${params}&limit=15`),
        apiClient.get<any>(`/activity/analytics/login-trend?${params}`),
      ]);

      if (dauRes.success) setDauMetrics(dauRes.data);
      if (loginRes.success) setLoginSummary(loginRes.data);
      if (detailRes.success) setLoginDetails(detailRes.data);
      if (sessionRes.success) setSessionSummary(sessionRes.data);
      if (pagesRes.success) setTopPages(pagesRes.data);
      if (trendRes.success) setLoginTrend(trendRes.data);
    } catch (err) {
      console.error('Failed to fetch activity data', err);
    } finally {
      setLoading(false);
    }
  }, [getDateParams, sortBy, roleFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const maxTrendLogins = Math.max(...loginTrend.map(t => t.logins), 1);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Monitor className="w-6 h-6" />
            User Activity
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Login frequency, session duration, and page views</p>
        </div>
        <div className="flex items-center gap-2">
          {(['7d', '14d', '30d', '90d'] as DateRange[]).map(range => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                dateRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-neutral-700'
              }`}
            >
              {range}
            </button>
          ))}
          <button onClick={fetchData} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* DAU/WAU/MAU Cards */}
      {dauMetrics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <MetricCard label="DAU" value={dauMetrics.dau} icon={Users} color="blue" />
          <MetricCard label="WAU (7d)" value={dauMetrics.wau} icon={TrendingUp} color="green" />
          <MetricCard label="MAU (30d)" value={dauMetrics.mau} icon={TrendingUp} color="purple" />
          <MetricCard
            label="Stickiness"
            value={dauMetrics.mau > 0 ? `${Math.round((dauMetrics.dau / dauMetrics.mau) * 100)}%` : '—'}
            icon={ArrowUpRight}
            color="amber"
            subtitle="DAU/MAU"
          />
        </div>
      )}

      {/* DAU by Role */}
      {dauMetrics && Object.keys(dauMetrics.dauByRole).length > 0 && (
        <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Today's Active Users by Role</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(dauMetrics.dauByRole).map(([role, count]) => (
              <span key={role} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${ROLE_COLORS[role] || 'bg-gray-100 text-gray-800'}`}>
                {ROLE_LABELS[role] || role}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Session + Login Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Login Summary */}
        {loginSummary && (
          <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
              <Users className="w-4 h-4" /> Login Summary
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{loginSummary.totalLogins}</p>
                <p className="text-xs text-gray-500">Total Logins</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{loginSummary.uniqueUsers}</p>
                <p className="text-xs text-gray-500">Unique Users</p>
              </div>
            </div>
            {Object.keys(loginSummary.byRole).length > 0 && (
              <div className="mt-4 space-y-2">
                {Object.entries(loginSummary.byRole).sort((a, b) => b[1] - a[1]).map(([role, count]) => (
                  <div key={role} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{ROLE_LABELS[role] || role}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Session Summary */}
        {sessionSummary && (
          <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4" /> Session Summary
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{sessionSummary.totalSessions}</p>
                <p className="text-xs text-gray-500">Total Sessions</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{sessionSummary.uniqueUsers}</p>
                <p className="text-xs text-gray-500">Unique Users</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatDuration(sessionSummary.avgDurationSeconds)}</p>
                <p className="text-xs text-gray-500">Avg Duration</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{sessionSummary.avgPageViews}</p>
                <p className="text-xs text-gray-500">Avg Pages/Session</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-neutral-700">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Median Duration</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatDuration(sessionSummary.medianDurationSeconds)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Login Trend */}
      {loginTrend.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Daily Login Trend
          </h3>
          <div className="flex items-end gap-[2px] h-32">
            {loginTrend.map((entry) => {
              const height = Math.max((entry.logins / maxTrendLogins) * 100, 2);
              return (
                <div key={entry.date} className="flex-1 flex flex-col items-center group relative">
                  <div
                    className="w-full bg-blue-500 dark:bg-blue-400 rounded-t transition-all hover:bg-blue-600"
                    style={{ height: `${height}%` }}
                  />
                  <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                    {entry.date}: {entry.logins} logins, {entry.uniqueUsers} users
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1 text-[10px] text-gray-400">
            <span>{loginTrend[0]?.date}</span>
            <span>{loginTrend[loginTrend.length - 1]?.date}</span>
          </div>
        </div>
      )}

      {/* Top Pages */}
      {topPages.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
            <Eye className="w-4 h-4" /> Top Pages
          </h3>
          <div className="space-y-2">
            {topPages.map((page, i) => {
              const maxViews = topPages[0]?.views || 1;
              const width = (page.views / maxViews) * 100;
              return (
                <div key={page.path} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-5 text-right">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-sm font-mono text-gray-900 dark:text-white truncate">{page.path}</span>
                      <span className="text-xs text-gray-500 ml-2 shrink-0">{page.views} views · {page.uniqueUsers} users</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 dark:bg-neutral-700 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: `${width}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* User Login Details Table */}
      <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <Users className="w-4 h-4" /> Per-User Login Activity
          </h3>
          <div className="flex items-center gap-2">
            <select
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
              className="text-sm border border-gray-300 dark:border-neutral-600 rounded-lg px-2 py-1.5 bg-white dark:bg-neutral-900 text-gray-700 dark:text-gray-300"
            >
              <option value="">All Roles</option>
              <option value="physician">Physician</option>
              <option value="distributor">Account Manager</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">Admin</option>
            </select>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as any)}
              className="text-sm border border-gray-300 dark:border-neutral-600 rounded-lg px-2 py-1.5 bg-white dark:bg-neutral-900 text-gray-700 dark:text-gray-300"
            >
              <option value="loginCount">Most Logins</option>
              <option value="lastLoginAt">Most Recent</option>
            </select>
          </div>
        </div>

        {loginDetails.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No login activity found for this period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-neutral-700">
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="text-right py-2 px-2 text-xs font-medium text-gray-500 uppercase">Logins</th>
                  <th className="text-right py-2 px-2 text-xs font-medium text-gray-500 uppercase">Last Login</th>
                </tr>
              </thead>
              <tbody>
                {loginDetails.map(user => (
                  <tr key={user.userId} className="border-b border-gray-100 dark:border-neutral-700/50 hover:bg-gray-50 dark:hover:bg-neutral-700/30">
                    <td className="py-2.5 px-2">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </td>
                    <td className="py-2.5 px-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[user.role] || 'bg-gray-100 text-gray-800'}`}>
                        {ROLE_LABELS[user.role] || user.role}
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-right font-mono font-medium text-gray-900 dark:text-white">
                      {user.loginCount}
                    </td>
                    <td className="py-2.5 px-2 text-right text-gray-500 text-xs">
                      {formatDate(user.lastLoginAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, color, subtitle }: {
  label: string;
  value: number | string;
  icon: any;
  color: string;
  subtitle?: string;
}) {
  const colorMap: Record<string, string> = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
  };

  return (
    <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorMap[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  );
}
